import { Box, HStack, Icon, Stat, StatHelpText, StatLabel, StatNumber, Text, usePrefersReducedMotion } from '@chakra-ui/react';
import { AlertTriangle, Stars01, ThumbsDown, ThumbsUp } from '@untitled-ui/icons-react';
import { useTranslation } from 'react-i18next';

import { AutomatedVerification } from '../aiVerification';

type Props = {
  verification: AutomatedVerification;
};

// Palette tokens only -- Chakra resolves gradient stops against theme.colors,
// so semantic tokens (success, error) would emit an invalid stop.
const rainbowGradient = 'conic(from 0deg, red.200, orange.200, yellow.200, green.200, blue.200, purple.200, red.200)';

// Rounds because the raw multiplication leaks float artifacts -- 0.29 * 100 is
// 28.999999999999996. Null when there is no usable number, so the readout can be
// dropped instead of rendering "NaN%".
const formatConfidencePercent = (confidence: number | null) =>
  typeof confidence === 'number' && Number.isFinite(confidence) ? Math.round(confidence * 100) : null;

export const AiVerification = ({ verification }: Props) => {
  const { t } = useTranslation();
  const prefersReducedMotion = usePrefersReducedMotion();

  const { overall_result: result, overall_confidence: confidence, reasoning_short: reasoningShort } = verification;

  const percent = formatConfidencePercent(confidence);
  const isRejected = result === 'reject';

  const icon = result === 'approve' ? ThumbsUp : isRejected ? ThumbsDown : AlertTriangle;
  const color = result === 'approve' ? 'success' : isRejected ? 'red.600' : 'warning';

  const getResultText = () => {
    if (result === 'error') {
      return t('b2c_verification:details_page.ai_verification.result_error');
    }
    if (percent === null) {
      return result === 'approve' ? t('b2c_verification:status.approved') : t('b2c_verification:status.rejected');
    }
    return result === 'approve'
      ? t('b2c_verification:details_page.ai_verification.result_approved', { confidence: percent })
      : t('b2c_verification:details_page.ai_verification.result_rejected', { confidence: percent });
  };

  return (
    <Box position="relative" overflow="hidden" borderRadius="lg" p="2px" mb="4">
      <Box
        position="absolute"
        top="50%"
        left="50%"
        mt="-110vmax"
        ml="-110vmax"
        boxSize="220vmax"
        bgGradient={rainbowGradient}
        animation={prefersReducedMotion ? undefined : 'rainbow-border-spin 4s linear infinite'}
      />

      <Box position="relative" bg="bg-surface" borderRadius="md" px="4" py="3">
        <Stat>
          <StatLabel>
            <HStack gap="1.5">
              <Icon as={Stars01} boxSize="4" />
              <Text>{t('b2c_verification:details_page.ai_verification.label')}</Text>
            </HStack>
          </StatLabel>

          <StatNumber color={color}>
            <HStack spacing="3">
              <Icon as={icon} boxSize="7" />
              <Text as="span">{getResultText()}</Text>
            </HStack>
          </StatNumber>

          <StatHelpText mb="0" mt="1">
            {reasoningShort || verification.error_message || t('b2c_verification:details_page.ai_verification.no_summary')}
          </StatHelpText>
        </Stat>
      </Box>
    </Box>
  );
};
